import {useState} from 'react';
import {QrCode, ShieldCheck, ShieldOff} from 'lucide-react';
import {toast} from 'sonner';
import {api, message} from '../../lib/api';
import {Avatar, Btn} from '../shared';
import {Switch} from '../ui/switch';

const CoachRow = ({connection, onChange}) => {
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const coach = connection.trainer || {};
  const toggle = async value => {
    setBusy(true);
    try { await api.patch(`/parent/connections/${connection.id}/passport-visibility`, {on_passport: value}); await onChange(); toast.success(value ? 'Shown on their coach’s Passport' : 'Hidden from their coach’s Passport'); }
    catch (e) { toast.error(message(e)); }
    finally { setBusy(false); }
  };
  const end = async () => {
    setBusy(true);
    try { await api.delete(`/parent/connections/${connection.id}`); setConfirm(false); await onChange(); toast.success('Connection ended'); }
    catch (e) { toast.error(message(e)); setBusy(false); }
  };
  return <div className="coach-row" data-testid={`coach-row-${connection.id}`}>
    <div className="coach-row-identity">
      <Avatar photo={coach.photo_id} name={coach.name || 'Coach'} testId={`coach-avatar-${connection.id}`} />
      <div><strong data-testid={`coach-name-${connection.id}`}>{coach.name || 'Coach'}</strong><p data-testid={`coach-title-${connection.id}`}>{coach.title}</p></div>
      <ShieldCheck size={17} color="#75b59a" />
    </div>
    <label className="coach-row-visibility">
      <span>Show {connection.athlete_first} on this coach’s public Passport</span>
      <Switch data-testid={`coach-passport-visibility-${connection.id}`} checked={connection.on_passport} disabled={busy}
        onCheckedChange={toggle} aria-label={`Show ${connection.athlete_first} on ${coach.name || 'this coach'}’s public Passport`} />
    </label>
    {confirm
      ? <div className="coach-row-confirm" role="alert" data-testid={`coach-end-confirm-${connection.id}`}>
        <span>End this connection? {coach.name || 'This coach'} loses access to {connection.athlete_first}’s records. Everything already written stays on your player’s record.</span>
        <div>
          <button data-testid={`coach-end-cancel-${connection.id}`} disabled={busy} onClick={() => setConfirm(false)}>Keep coach</button>
          <button className="coach-end-yes" data-testid={`coach-end-yes-${connection.id}`} disabled={busy} onClick={end}>End connection</button>
        </div>
      </div>
      : <button className="coach-row-end" data-testid={`coach-end-${connection.id}`} onClick={() => setConfirm(true)}><ShieldOff size={14} />End connection</button>}
  </div>;
};

export const CoachingTeam = ({players, connections, onChange, onInvite}) => {
  const first = name => (name || 'Your player').trim().split(/\s+/)[0];
  const coaches = connections.filter((c, i, all) => all.findIndex(x => x.trainer_id === c.trainer_id) === i);
  return <section className="parent-connections" data-testid="parent-coaching-team">
    <div className="section-heading"><h2>Your coaching team</h2>{players.length > 0 && <Btn secondary data-testid="invite-coach-button" onClick={onInvite}><QrCode />Invite a coach</Btn>}</div>
    {players.map(player => {
      const forPlayer = connections.filter(c => c.athlete_id === player.id)
        .map(c => ({...c, athlete_first: first(player.name)}));
      return <div className="coach-group" data-testid={`coach-group-${player.id}`} key={player.id}>
        <h3 data-testid={`coach-group-name-${player.id}`}>{player.name}</h3>
        {forPlayer.length
          ? <div className="coach-group-list">{forPlayer.map(c => <CoachRow key={c.id} connection={c} onChange={onChange} />)}</div>
          : <p className="page-description" data-testid={`coach-group-empty-${player.id}`}>No coaches yet. Show a coach the QR code from <strong>Invite a coach</strong>, or open a coach’s Passport link.</p>}
      </div>;
    })}
    {players.length > 0 && coaches.length === 0 && <p className="form-hint" data-testid="no-coaches-note">Every connection starts with a parent. You stay in control of who sees your player.</p>}
  </section>;
};
